import React, { useState } from 'react'
import { Portal } from 'react-portal'
import { useThemeContext } from '@sketch/global-styles'
import {
  Box,
  Flex,
  Popover,
  useBreakpoint,
  Modal,
  ModalTransition,
} from '@sketch/components'
import { StyledDropdownItem, Icon, ArrowIcon } from './UserDropdown.styles'
import { ReactComponent as ContrastIcon } from '@sketch/icons/contrast-16'
import { ReactComponent as SunIcon } from '@sketch/icons/sun-16'
import { ReactComponent as MoonIcon } from '@sketch/icons/moon-16'
import { ReactComponent as DeviceDesktopIcon } from '@sketch/icons/device-desktop-16'
import {
  MobileNavigationHeader,
  TallerModalHeader,
  SelectDropdownModalFooter,
  SelectDropdownCancelButton,
  BackButton,
  BackIcon,
} from './AppearanceMenu.styles'

export const AppearanceMenu = () => {
  const {
    enableLightMode,
    enableDarkMode,
    toggleMatchSystem,
    theme,
    matchSystem,
  } = useThemeContext()

  const isMobileAndBigger = useBreakpoint('xs')
  const isMobile = !isMobileAndBigger

  const [isVisible, setIsVisible] = useState(false)
  const [hoveringOptions, setIsHoveringOptions] = useState(false)
  let timeout: ReturnType<typeof setTimeout> | null = null

  const isLight = !matchSystem && theme === 'light'
  const isDark = !matchSystem && theme === 'dark'

  const menuContent = (
    <>
      <StyledDropdownItem $isActive={isLight} onClick={enableLightMode}>
        <Icon $isActive={isLight} as={SunIcon} />
        Light
      </StyledDropdownItem>
      <StyledDropdownItem $isActive={isDark} onClick={enableDarkMode}>
        <Icon $isActive={isDark} as={MoonIcon} />
        Dark
      </StyledDropdownItem>
      <StyledDropdownItem $isActive={matchSystem} onClick={toggleMatchSystem}>
        <Icon $isActive={matchSystem} as={DeviceDesktopIcon} />
        System Default
      </StyledDropdownItem>
    </>
  )

  if (isMobile) {
    // mobile menu
    return (
      <>
        <StyledDropdownItem
          onClick={() => setIsVisible(true)}
          aria-haspopup
          aria-expanded={isVisible}
        >
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Flex alignItems="flex-end">
              <Icon as={ContrastIcon} /> Appearance
            </Flex>
            <ArrowIcon themeMode={theme} />
          </Flex>
        </StyledDropdownItem>
        <Portal>
          <ModalTransition show={isVisible}>
            <Modal onCancel={() => setIsVisible(false)}>
              <TallerModalHeader as={MobileNavigationHeader}>
                <BackButton onClick={() => setIsVisible(false)}>
                  <BackIcon />
                  Appearance
                </BackButton>
              </TallerModalHeader>
              <Modal.Body>{menuContent}</Modal.Body>
              <SelectDropdownModalFooter>
                <SelectDropdownCancelButton
                  variant="tertiary"
                  size="40"
                  onClick={() => setIsVisible(false)}
                  fill
                >
                  Cancel
                </SelectDropdownCancelButton>
              </SelectDropdownModalFooter>
            </Modal>
          </ModalTransition>
        </Portal>
      </>
    )
  }

  const handleOnMouseEnter = () => setIsVisible(true)

  const handleOnMouseLeave = () => {
    // setTimeout so the user can actually reach the options section
    timeout = setTimeout(() => {
      if (hoveringOptions) {
        return
      }

      setIsVisible(false)
    }, 150)
  }

  const handleOnEnterOptions = () => {
    setIsHoveringOptions(true)

    if (timeout !== null) {
      clearTimeout(timeout)
    }
  }

  const handleOnLeaveOptions = () => {
    setIsHoveringOptions(false)
    setIsVisible(false)
  }

  return (
    <Popover
      placement="left"
      spacing="-8px"
      popup={
        <Box
          pt={2}
          pb={2}
          onMouseEnter={handleOnEnterOptions}
          onMouseLeave={handleOnLeaveOptions}
        >
          {menuContent}
        </Box>
      }
      visible={isVisible}
    >
      <StyledDropdownItem
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      >
        <Flex width="100%" justifyContent="space-between" alignItems="flex-end">
          <Flex alignItems="flex-end">
            <Icon as={ContrastIcon} /> Appearance
          </Flex>
          <ArrowIcon themeMode={theme} />
        </Flex>
      </StyledDropdownItem>
    </Popover>
  )
}
